import { useState } from "react";
import { Group, Button, ActionIcon, Select, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconEdit,
  IconSend,
  IconRefresh,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { DataTable } from "../../DataTable";
import {
  TableAction,
  TableActions,
  DeleteAction,
  actionsColumn,
} from "../../TableActions";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { useConfirm } from "../../../hooks/useConfirm";
import {
  useGetInvites,
  useGetInviteStats,
  useDeleteInviteRecipient,
  useResendInvites,
} from "../../../api/invites";
import InviteStatusBadge, { INVITE_STATUS_OPTIONS } from "./InviteStatusBadge";
import EditRecipientModal from "./EditRecipientModal";

const PAGE_SIZE = 10;

const RecipientsTable = ({ eventId }) => {
  const [page, setPage] = useState(1);
  const [editRecipient, setEditRecipient] = useState(null);
  const [sorting, setSorting] = useState({
    columnAccessor: "createdAt",
    direction: "desc",
  });
  const [filter, setFilter] = useState(null);
  const [debouncedFilter] = useDebouncedValue(filter, 200);
  const confirm = useConfirm();

  const handleSortChange = (sort) => {
    setPage(1);
    setSorting(sort);
  };

  const handleFilterChange = (value) => {
    setPage(1);
    setFilter(value);
  };

  const { data, isPending } = useGetInvites({
    eventId,
    page,
    limit: PAGE_SIZE,
    sort: JSON.stringify(sorting),
    filter: debouncedFilter,
  });
  const { data: stats } = useGetInviteStats(eventId);

  const { mutate: deleteRecipient } = useDeleteInviteRecipient(eventId);
  const confirmDelete = useDeleteConfirm("recipient", deleteRecipient);
  const { mutate: resendInvites, isPending: isResending } =
    useResendInvites(eventId);

  const records = data?.data || [];
  const totalRecords = data?.pagination?.total || 0;

  const undeliveredCount = Number(stats?.bounced ?? 0);

  // Resending queues background send jobs, so the notification reports what was
  // queued — the rows move Queued -> Sent on their own as the jobs run.
  const resend = (body) =>
    resendInvites(body, {
      onSuccess: ({ queued, skipped }) => {
        notifications.show({
          color: queued > 0 ? "green" : "yellow",
          title: queued > 0 ? "Invites queued" : "Nothing to resend",
          message:
            skipped > 0
              ? `${queued} queued for resend, ${skipped} skipped (sent too recently).`
              : `${queued} queued for resend.`,
        });
      },
    });

  const resendOne = (recipient) =>
    confirm({
      title: "Resend invite?",
      message: `Resend the invite email to ${recipient.email}.`,
      confirmLabel: "Resend",
      onConfirm: () => resend({ recipientIds: [recipient.id] }),
    });

  const resendUndelivered = () =>
    confirm({
      title: "Resend undelivered invites?",
      message: `Resend the invite email to ${undeliveredCount} bounced recipient${
        undeliveredCount === 1 ? "" : "s"
      }.`,
      confirmLabel: "Resend",
      onConfirm: () => resend({ filter: "undelivered" }),
    });

  const columns = [
    {
      accessor: "email",
      title: "Email",
      sortable: true,
      filter: (
        <FilterSearch
          query={filter}
          setQuery={handleFilterChange}
          columnName="email"
        />
      ),
      filtering: filter?.email,
    },
    {
      accessor: "firstName",
      title: "First name",
      sortable: true,
      render: (r) => r.firstName || "-",
      filter: (
        <FilterSearch
          query={filter}
          setQuery={handleFilterChange}
          columnName="firstName"
        />
      ),
      filtering: filter?.firstName,
    },
    {
      accessor: "lastName",
      title: "Last name",
      sortable: true,
      render: (r) => r.lastName || "-",
      filter: (
        <FilterSearch
          query={filter}
          setQuery={handleFilterChange}
          columnName="lastName"
        />
      ),
      filtering: filter?.lastName,
    },
    {
      accessor: "status",
      title: "Status",
      sortable: true,
      render: (r) => <InviteStatusBadge status={r.status} />,
      filter: <StatusFilter query={filter} setQuery={handleFilterChange} />,
      filtering: filter?.status,
    },
    actionsColumn((recipient) => (
      <TableActions>
        {/* 'imported' hasn't been sent yet (that's the composer's Send), and
            'sending' is already claimed by a job — neither can be resent. */}
        {!["imported", "sending"].includes(recipient.status) && (
          <TableAction
            label="Resend"
            icon={IconSend}
            onClick={() => resendOne(recipient)}
            disabled={isResending}
          />
        )}
        <TableAction
          label="Edit"
          icon={IconEdit}
          onClick={() => setEditRecipient(recipient)}
        />
        <DeleteAction onDelete={() => confirmDelete({ id: recipient.id })} />
      </TableActions>
    )),
  ];

  return (
    <>
      {undeliveredCount > 0 && (
        <Group justify="flex-end">
          <Button
            variant="default"
            leftSection={<IconRefresh size={16} />}
            onClick={resendUndelivered}
            loading={isResending}
          >
            Resend undelivered ({undeliveredCount})
          </Button>
        </Group>
      )}

      <DataTable
        columns={columns}
        records={records}
        fetching={isPending}
        page={page}
        onPageChange={setPage}
        totalRecords={totalRecords}
        recordsPerPage={PAGE_SIZE}
        sortStatus={sorting}
        onSortStatusChange={handleSortChange}
      />

      <EditRecipientModal
        opened={!!editRecipient}
        onClose={() => setEditRecipient(null)}
        eventId={eventId}
        recipient={editRecipient}
      />
    </>
  );
};

function FilterSearch({ query, setQuery, columnName }) {
  const readableColumnName = columnName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return (
    <TextInput
      label="Recipients"
      description={`Show recipients whose ${readableColumnName} includes the specified text`}
      placeholder="Search recipients..."
      leftSection={<IconSearch size={16} />}
      rightSection={
        <ActionIcon
          size="sm"
          variant="transparent"
          c="dimmed"
          onClick={() => setQuery(null)}
        >
          <IconX size={14} />
        </ActionIcon>
      }
      value={query?.[columnName] || ""}
      onChange={(e) => {
        if (e.currentTarget.value === "") {
          setQuery(null);
          return;
        }
        setQuery({ [columnName]: e.currentTarget.value });
      }}
    />
  );
}

function StatusFilter({ query, setQuery }) {
  return (
    <Select
      label="Status"
      description="Filter recipients by status"
      placeholder="Filter by status"
      data={INVITE_STATUS_OPTIONS}
      value={query?.status || null}
      onChange={(value) => {
        setQuery({ status: value });
      }}
      onClear={() => setQuery(null)}
      clearable
    />
  );
}

export default RecipientsTable;
